import { isMonday, isSunday, previousMonday, lastDayOfMonth, addDays, format, isEqual, nextSunday, isSameDay } from "date-fns"
import { IEnkCalendarEvent } from "./calendar-types"

export type DayInfo = { date: string; isCurrentMonth?: boolean; isToday?: boolean; isSelected?: boolean; events?: IEnkCalendarEvent[] }
export type MonthInfo = { name: string; days: DayInfo[] }[]

export function generateDaysOfMonth(year: number, month: number, selectedDate?: Date): DayInfo[] {
  const days: DayInfo[] = []
  try {
    const start = new Date(year, month)
    const end = lastDayOfMonth(start)
    if (isNaN(start.getTime())) {
      throw new Error(`Invalid date: ${year}-${month}`)
    }
    //dagen voor de maand, in dezelfde week
    const monday = previousMonday<Date>(start)
    if (!isMonday(start)) {
      const daysInPrevMonth = lastDayOfMonth(monday).getDate() - monday.getDate()
      for (let d = 0; d <= daysInPrevMonth; d++) {
        const day = addDays(monday, d)
        days.push({
          date: format(day, "yyyy-MM-dd"),
          isCurrentMonth: false,
          isToday: false,
          isSelected: false,
        })
      }
    }
    //dagen van de maand
    for (let d = 0; d < end.getDate(); d++) {
      const day = addDays(start, d)
      days.push({
        date: format(day, "yyyy-MM-dd"),
        isCurrentMonth: true,
        isToday: isSameDay(day, new Date()),
        isSelected: selectedDate ? isSameDay(day, selectedDate) : false,
      })
      if (isEqual(day, new Date())) {
        console.log("Today is in the previous month")
      }
    }
    //dagen na de maand, in dezelfde week
    const sunday = nextSunday(end)
    if (!isSunday(end)) {
      for (let d = 1; d <= sunday.getDate(); d++) {
        const day = addDays(end, d)
        days.push({
          date: format(day, "yyyy-MM-dd"),
          isCurrentMonth: false,
          isToday: false,
          isSelected: false,
        })
      }
    }
  } catch (error) {
    console.error("Error generating month data:", error)
  }

  return days
}

export function generateMonths(year: number, selectedDate: Date, locale?: string): MonthInfo {
  const months = []
  const monthNames = Array.from({ length: 12 }, (_, i) => new Date(0, i).toLocaleString(locale, { month: "long" }))

  for (let month = 0; month < 12; month++) {
    const days = generateDaysOfMonth(year, month, selectedDate)
    months.push({
      name: monthNames[month],
      days,
    })
  }

  return months
}

export function getDaysAroundDate(date: Date, daysBefore: number, daysAfter: number): DayInfo[] {
  const days = []
  for (let i = -daysBefore; i <= daysAfter; i++) {
    const day = addDays(date, i)
    days.push({
      date: format(day, "yyyy-MM-dd"),
      isCurrentMonth: true, // Assuming the days around the date are in the current month
      isToday: isSameDay(day, new Date()),
      isSelected: i == 0, // the day in the center is always the selected date
    })
  }
  return days as DayInfo[]
}

export function getWeekDaysOfWeek(date: Date): DayInfo[] {
  const monday = isMonday(date) ? date : previousMonday<Date>(date)

  const days = Array.from({ length: 7 }, (_, i) => {
    const day = addDays(monday, i)
    return {
      date: format(day, "yyyy-MM-dd"),
      isCurrentMonth: true, // Assuming the days around the date are in the current month
      isToday: isSameDay(day, new Date()),
      isSelected: isSameDay(day, date),
    }
  })
  return days as DayInfo[]
}

export function getDayNames(locale: string): string[] {
  return Array.from({ length: 7 }, (_, i) => new Date(0, 0, i + 1).toLocaleString(locale, { weekday: "short" }))
}

export function getDayName(locale: string, day: DayInfo): string {
  return new Date(day.date).toLocaleString(locale, { weekday: "short" })
}

export function getDayNumber(day: DayInfo): string {
  return day.date.split("-").pop()?.replace(/^0/, "") || ""
}
